/**
 * @file RiskQuiz.jsx
 * @description Manages new user setup and profile creation
 * 
 * @section Responsibilities
 * - Collect user information
 * - Handle document uploads
 * - Validate profile data
 * - Track onboarding progress
 * 
 * @section Dependencies
 * - Profile Management: User data handling
 * - Document Service: File handling
 * - Validation Service: Data validation
 * 
 * @section Integration Points
 * - Profile API: Data submission
 * - Document Service: File handling
 * - Onboarding Flow: Progress tracking
 * 
 * @section State Management
 * - profileData: User information
 * - validationState: Form validation
 * - progress: Onboarding progress
 * 
 * @section Events
 * - onProfileUpdate: Profile changes
 * - onDocumentUpload: File handling
 * - onValidation: Form validation
 * 
 * @section Props
 * @prop {Object} initialData - Initial profile data
 * @prop {Function} onComplete - Completion callback
 */

import axios from "axios";
import React, { useRef, useState, useEffect } from "react";
import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/risk-quiz.css";
import { useForm } from "../../utils/useForm";
import Question from "../../components/Question";
import DgButton from "../../components/DgButton";
import { useNavigate } from "react-router-dom";
import BallsBackground from "../../components/BallsBackground";

/**
 * @file RiskQuiz.jsx
 * @description Assesses user's risk tolerance through a dynamic questionnaire
 *
 * @section Responsibilities
 * - Present risk assessment questions
 * - Calculate risk tolerance score
 * - Track quiz progress
 * - Handle question navigation
 * - Store assessment results
 * - Provide risk profile recommendations
 *
 * @section Dependencies
 * - Question Management: Question flow and logic
 * - Scoring System: Risk calculation
 * - Progress Tracking: Quiz completion status
 * - Profile Management: Risk profile storage
 *
 * @section Integration Points
 * - Risk Assessment API: Score calculation
 * - Profile Service: Risk profile updates
 * - Onboarding Flow: Progress tracking
 * - Strategy Selection: Risk-based recommendations
 *
 * @section State Management
 * - currentQuestion: Active question state
 * - answers: User responses
 * - progress: Quiz completion status
 * - score: Risk tolerance calculation
 * - loading: Assessment processing
 *
 * @section Events
 * - onAnswer: Question response
 * - onProgress: Quiz advancement
 * - onComplete: Quiz completion
 * - onError: Assessment errors
 *
 * @section Props
 * @prop {Function} onComplete - Callback after quiz completion
 * @prop {Object} initialData - Previous assessment data
 * @prop {Boolean} isEditMode - Controls edit/view mode
 */

function useElementSize(element) {
  const [elementSize, setElementSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setElementSize({
        width: element.current ? element.current.offsetWidth : 0,
        height: element.current ? element.current.offsetHeight : 0,
      });
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return elementSize;
}

function RiskQuiz() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.session);

  const [questions, setQuestions] = useState(
    require("../../assets/json/questions.json")
  );

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [data, setData] = useForm({
    score: 325,
  });

  const selectOption = (index, id) => {
    setError("");
    const question = questions[index];
    question.answers.forEach((answer) => (answer.checked = answer.id == id));
    setQuestions([...questions]);
  };

  const validateAnswers = () => {
    for (const question of questions) {
      if (question.answers.every((answer) => !answer.checked)) {
        setError("Please answer all questions");
        return false;
      }
    }
    return true;
  };

  const updateUser = () => {
    const isValid = validateAnswers();
    if (!isValid) return;

    setLoading(true);

    axios
      .put(`${process.env.REACT_APP_BASE_URL}/user`, {
        PK: user.PK,
        SK: user.SK,
        ...data,
      })
      .then((res) => {
        navigate("/onboarding/strategy");
      });
  };

  const [isVisible, setIsVisible] = useState(true);
  const stickyRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const { top } = stickyRef.current.getBoundingClientRect();
      if (top <= 0) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="risk-quiz">
      <BallsBackground>
        <div className="content container shadow1">
          <div className="progress-bar step2">
            <div className="progress"></div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>1</span>
                </div>
                <p>Your profile</p>
              </div>
            </div>
            <div className="step active">
              <div className="title">
                <div className="number">
                  <span>2</span>
                </div>
                <p>Personalization</p>
              </div>
            </div>
            <div className="step">
              <div className="title">
                <div className="number">
                  <span>3</span>
                </div>
                <p>Disclosures</p>
              </div>
            </div>
          </div>
          <div className="quiz-header" ref={stickyRef}>
            <div
              className={isVisible ? "visible heading" : "not-visible heading"}
            >
              <p className="subtitle">Step 1</p>
              <h1 className="title">Confirmation</h1>
              <p className="description">Enter 4 digits code</p>
            </div>
            <h2 className="risk-title">Risk Assessment</h2>
            <div className="line"></div>
          </div>
          <div className="inner-content">
            {questions.map((ques, index) => (
              <Question
                data={ques}
                key={index}
                index={index}
                action={selectOption}
              />
            ))}
            <div className="button-container">
              {error && <div className="error">{error}</div>}
              <DgButton
                category={"primary"}
                size={"large"}
                text={"Next"}
                action={updateUser}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </BallsBackground>
    </div>
  );
}

export default RiskQuiz;
